import { useEffect, useRef } from "react";

// Click-and-drag panning for scrollable grids.
//   const dragRef = useDragScroll();
//   <div ref={dragRef} className="overflow-auto">…</div>
//
// - Left-button drag pans both axes (grab/grabbing cursor).
// - Drags may start on buttons/cells; if the pointer moved more than the
//   threshold, the resulting click is swallowed so cells don't toggle
//   accidentally after a pan.
// - Inputs/selects/textareas are exempt so typing and text selection work.
export default function useDragScroll() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let dragging = false;
    let moved = false;
    let startX = 0, startY = 0, startLeft = 0, startTop = 0;

    const onDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest("input, textarea, select, [contenteditable]")) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      startLeft = el.scrollLeft;
      startTop = el.scrollTop;
    };

    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!moved && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
        moved = true;
        el.classList.add("drag-scrolling");
      }
      if (moved) {
        el.scrollLeft = startLeft - dx;
        el.scrollTop = startTop - dy;
        e.preventDefault();
      }
    };

    const onUp = () => {
      dragging = false;
      el.classList.remove("drag-scrolling");
    };

    // Capture-phase click: swallow the click that ends a drag.
    const onClick = (e) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    };

    el.classList.add("drag-scroll");
    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("click", onClick, true);
    return () => {
      el.classList.remove("drag-scroll", "drag-scrolling");
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("click", onClick, true);
    };
  }, []);

  return ref;
}
